import React, { useMemo } from 'react';
import { FormField, MultiSelect } from '@/shared/components/ui';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';

export interface ProjectMembersFieldProps {
  memberIds: string[];
  leaderId: string;
  onChange: (ids: string[]) => void;
  /** Prefixo dos data-testid (default `project-members`). */
  testIdPrefix?: string;
}

/**
 * Campo de "Equipe" — membros do projeto (exclui o líder das opções).
 * Presentacional, reutilizado no formulário de criação e na aba de config.
 */
export const ProjectMembersField: React.FC<ProjectMembersFieldProps> = ({
  memberIds,
  leaderId,
  onChange,
  testIdPrefix = 'project-members',
}) => {
  const { data: users, isLoading: loadingUsers } = useUsersDropdown();

  // Membros: exclui o líder (não pode ser membro e líder simultaneamente).
  const memberOptions = useMemo(
    () =>
      (users ?? [])
        .filter((u) => u.id !== leaderId)
        .map((u) => ({ value: u.id, label: u.name })),
    [users, leaderId],
  );

  // Nome do líder para o hint (o líder já participa e não aparece na lista).
  const leaderName = useMemo(
    () => (users ?? []).find((u) => u.id === leaderId)?.name ?? '',
    [users, leaderId],
  );

  return (
    <FormField label="Membros">
      <div data-testid={testIdPrefix}>
        <MultiSelect
          options={memberOptions}
          selectedValues={memberIds}
          onChange={onChange}
          loading={loadingUsers}
          placeholder="Selecione os membros"
        />
      </div>
      {leaderName && (
        <p className="text-xs mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>
          O líder ({leaderName}) não aparece aqui — ele já participa do projeto.
        </p>
      )}
    </FormField>
  );
};

ProjectMembersField.displayName = 'ProjectMembersField';
